import { Field } from "@/types/field";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import {
    Button,
    Selection,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    Divider,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faCheck,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import SITrendline from "@/components/field/insights/SITrendline";
import DateInput from "@/components/inputs/DateInput";
import StatusTimeline from "@/components/home/StatusTimeline";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { useTranslation } from "react-i18next";

function PreviewDrawer({
    drawerOpen,
    onDrawerOpen,
    onDrawerClose,
    setSelectedKeys,
    selectedField,
}: {
    drawerOpen: boolean;
    onDrawerOpen: () => void;
    onDrawerClose: () => void;
    setSelectedKeys: (keys: Selection) => void;
    selectedField: Field;
}) {
    const { t } = useTranslation("home-field");
    const { notificationsV2Enabled } = useFeatureFlag();
    const f = useForm({
        defaultValues: {
            startDate: getTodayWithTimeTravel().subtract(1, "month").toDate(),
            endDate: getTodayWithTimeTravel().toDate(),
        },
    });

    const [startDate, endDate] = f.watch(["startDate", "endDate"]);
    const lastTwoMonths = useMemo(() => {
        if (!startDate || !endDate) return false;

        return dayjs(startDate).add(2, "months").isSame(dayjs(endDate), "days");
    }, [endDate, startDate]);

    const lastTwoWeeks = useMemo(() => {
        if (!startDate || !endDate) return false;

        return dayjs(startDate).add(2, "weeks").isSame(dayjs(endDate), "days");
    }, [endDate, startDate]);

    return (
        <Drawer
            isOpen={drawerOpen}
            onOpenChange={onDrawerOpen}
            onClose={() => {
                onDrawerClose();
                setSelectedKeys(new Set([]));
            }}
            size="3xl"
            scrollBehavior="inside"
        >
            <DrawerContent>
                <DrawerHeader>
                    <div className="flex flex-col gap-2 items-start">
                        <div className="font-semibold">
                            {selectedField.name}
                        </div>
                        <div className="text-xs ">{selectedField.grower}</div>
                    </div>
                </DrawerHeader>
                <DrawerBody>
                    <div>
                        <Button
                            fullWidth
                            as={Link}
                            to={`/dashboard/fields/$fieldId/insights/?cameFrom=home`}
                            params={
                                {
                                    fieldId: selectedField.id.toString(),
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any
                            }
                            startContent={
                                <FontAwesomeIcon
                                    icon={faArrowUpRightFromSquare}
                                />
                            }
                            color="primary"
                        >
                            {t("go_to_field_dashboard")}
                        </Button>
                    </div>

                    <FormProvider {...f}>
                        <div className="flex flex-row self-end justify-between w-full  gap-2 items-center">
                            <div className="font-semibold text-sm">
                                {t("date_range")}
                            </div>
                            <div className="flex flex-row gap-2">
                                <Button
                                    color={
                                        lastTwoMonths ? "success" : "default"
                                    }
                                    variant="flat"
                                    size="sm"
                                    onPress={() => {
                                        f.setValue(
                                            "startDate",
                                            dayjs()
                                                .subtract(2, "months")
                                                .toDate(),
                                        );
                                        f.setValue("endDate", dayjs().toDate());
                                    }}
                                >
                                    {lastTwoMonths && (
                                        <FontAwesomeIcon icon={faCheck} />
                                    )}
                                    {t("last_two_months")}
                                </Button>
                                <Button
                                    variant="flat"
                                    color={lastTwoWeeks ? "success" : "default"}
                                    size="sm"
                                    onPress={() => {
                                        f.setValue(
                                            "startDate",
                                            dayjs()
                                                .subtract(2, "weeks")
                                                .toDate(),
                                        );
                                        f.setValue("endDate", dayjs().toDate());
                                    }}
                                >
                                    {lastTwoWeeks && (
                                        <FontAwesomeIcon icon={faCheck} />
                                    )}
                                    {t("last_two_weeks")}
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-row justify-evenly  gap-2">
                            <DateInput
                                name="startDate"
                                label={t("start_date")}
                                size="sm"
                            />
                            <DateInput
                                name="endDate"
                                label={t("end_date")}
                                size="sm"
                            />
                        </div>
                        <SITrendline
                            fieldId={selectedField.id.toString()}
                            senstiivity={selectedField.insight_sensitivity}
                            disableResize
                        />
                    </FormProvider>
                    <Divider />
                    {notificationsV2Enabled && (
                        <StatusTimeline fieldId={selectedField.id} />
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

export default PreviewDrawer;
